import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useAppSettingsQuery } from '@queries/AppQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

interface SendEntertainerMediaBadgePopupContextValue {
  openSendEntertainerMediaBadgePopup: (updatedValues: {
    entertainerId: string;
    entertainerMediaId: string;
    onAfterSend: () => void;
  }) => void;
}

const sendEntertainerMediaBadgePopupContext =
  createContext<SendEntertainerMediaBadgePopupContextValue>({
    openSendEntertainerMediaBadgePopup: () => {},
  });

export const useSendEntertainerMediaBadgePopup = () => {
  return useContext(sendEntertainerMediaBadgePopupContext);
};

const SendEntertainerMediaBadgePopup: FC<{
  entertainerId: string;
  entertainerMediaId: string;
  onClose: () => void;
  onAfterSend: () => void;
}> = ({ entertainerId, entertainerMediaId, onClose, onAfterSend }) => {
  const { appSettings } = useAppSettingsQuery();
  const { createAuthGateHandler } = useAuthGate();
  const { sendEntertainerBadgeAsync } = useSendEntertainerBadge();

  const isVisible = !!entertainerId && !!entertainerMediaId;

  const { track } = useAnalytics();

  const handleSubmit = (badgeId: string) => {
    track('Send Media Badge', {
      entertainerId,
      entertainerMediaId,
      badgeId,
    });

    createAuthGateHandler(async () => {
      await sendEntertainerBadgeAsync({
        entertainerId,
        entertainerMediaId,
        badgeId,
        reason: 'Look',
      });
      onAfterSend();
      onClose();
    });
  };

  return (
    <Popup isVisible={isVisible} onClose={onClose} style={{ zIndex: 99999999 }}>
      <Popup.Content>
        <TitleToolbar text="Reward a Badge" size="md" />
        <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {appSettings?.allEntertainerMediaBadges?.map((badge) => (
            <div
              key={badge._id}
              className="d-flex gap-1 flex-column align-items-center text-center col-3"
              onClick={() => handleSubmit(badge._id)}
              role="button"
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  marginBottom: 6,
                }}
              >
                <img src={badge.imageUrl} style={{ margin: '2px auto', height: 32 }} />
              </div>
              <div style={{ fontSize: 10, lineHeight: 1 }}>{badge.title}</div>
            </div>
          ))}
        </div>
      </Popup.Content>
    </Popup>
  );
};

export const SendEntertainerMediaBadgePopupProvider = ({ children }: { children: ReactNode }) => {
  const { track } = useAnalytics();
  const [values, setValues] = useState({
    entertainerId: '',
    entertainerMediaId: '',
    onAfterSend: () => {},
  });

  const openSendEntertainerMediaBadgePopup = (updatedValues: {
    entertainerId: string;
    entertainerMediaId: string;
    onAfterSend: () => void;
  }) => {
    track('Open Send Entertainer Media Badge Popup', updatedValues);
    setValues(updatedValues);
  };

  return (
    <sendEntertainerMediaBadgePopupContext.Provider value={{ openSendEntertainerMediaBadgePopup }}>
      {children}
      <SendEntertainerMediaBadgePopup
        {...values}
        onClose={() =>
          setValues({ entertainerId: '', entertainerMediaId: '', onAfterSend: () => {} })
        }
      />
    </sendEntertainerMediaBadgePopupContext.Provider>
  );
};
